import { useEffect, useState } from 'react';

/**
 * Modal somente-leitura que carrega o documento ativo (Termos de Uso ou
 * Política de Privacidade) via GET público `/documentos-legais/publico/:tipo`
 * (REQ-012 Adendo / AB#63). Usado nos links do `/register` e `/login`.
 *
 * Diferente do `AceitePendenteModal` (bloqueante após MAJOR), este pode ser
 * fechado a qualquer momento — é apenas referencial.
 */

const TIPO_LABEL: Record<string, string> = {
  TERMOS_USO: 'Termos de Uso',
  PRIVACIDADE: 'Política de Privacidade',
  DPA: 'Data Processing Agreement',
};

interface Documento {
  found: boolean;
  versao?: string;
  publicado_em?: string;
  conteudo?: string;
}

export function DocumentoLegalModal({
  tipo,
  aberto,
  onFechar,
}: {
  tipo: 'TERMOS_USO' | 'PRIVACIDADE' | 'DPA';
  aberto: boolean;
  onFechar: () => void;
}): JSX.Element | null {
  const [doc, setDoc] = useState<Documento | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!aberto) return;
    let cancelado = false;
    (async () => {
      setCarregando(true);
      setErro('');
      try {
        const API_URL = `${import.meta.env.VITE_API_URL ?? 'http://localhost:8000'}/api`;
        const res = await fetch(`${API_URL}/documentos-legais/publico/${tipo}`);
        if (!res.ok || cancelado) return;
        const data = (await res.json()) as Documento;
        if (!cancelado) setDoc(data.found ? data : null);
      } catch {
        if (!cancelado) setErro('Falha ao carregar o documento.');
      } finally {
        if (!cancelado) setCarregando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [aberto, tipo]);

  if (!aberto) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onFechar}
      // [Bug#341] z acima do `ui/Modal` (z-[100]) e dos popovers (z-[110]): este
      // modal é SEMPRE aberto de dentro de outro modal (GoogleAuthSection,
      // Register, AtualizacaoMinorBanner). Com z-[90] ele abria ATRÁS do modal
      // que o invocou. Abaixo do Toast (z-[200]), que segue por cima de tudo.
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/60 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{TIPO_LABEL[tipo]}</h2>
            {doc?.versao && (
              <p className="mt-0.5 text-xs text-slate-500">
                Versão {doc.versao}
                {doc.publicado_em && (
                  <> · publicada em {new Date(doc.publicado_em).toLocaleDateString('pt-BR')}</>
                )}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onFechar}
            aria-label="Fechar"
            className="text-2xl leading-none text-slate-400 hover:text-slate-800"
          >
            ×
          </button>
        </header>
        <div className="flex-1 overflow-auto px-6 py-4">
          {carregando && <p className="text-sm text-slate-400">Carregando…</p>}
          {erro && (
            <p role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {erro}
            </p>
          )}
          {!carregando && !erro && !doc && (
            <p className="text-sm text-slate-500">Nenhuma versão publicada deste documento.</p>
          )}
          {doc?.conteudo && (
            <article
              className="prose prose-sm max-w-none text-slate-800"
              dangerouslySetInnerHTML={{ __html: doc.conteudo }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
